import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useRouteLoaderData } from '@remix-run/react';
import { usePopupMenuData } from './hooks/use-popup-menu-data.ts';
import { usePopupMenuPositioning } from './hooks/use-popup-menu-positioning.ts';
import { usePopupMenuDismissal } from './hooks/use-popup-menu-dismissal.ts';
import { useKeyboardInteraction } from './hooks/use-keyboard-interaction.ts';
import { useNestedNavigation } from './hooks/use-nested-navigation.ts';
import {
  PopupMenuOverlay,
  getPopupMenuOverlayMode,
  type OutsidePressRefs,
  type PopupMenuOverlayMode,
} from './overlay/index.ts';
import {
  getCreateNewPlaylistAssetSrc,
  getPlaylistSelectionAssetSrc,
} from './hooks/use-popup-menu-playlist.ts';
import {
  addInflateDataToCloudEventActions,
  registerPlaylistPopupActions,
} from './actions/register-playlist-popup-actions.ts';
import { PlaylistVarsScope } from '~/services/plugins/playlists-plugin/config/components/index.tsx';
import './popup-menu.css';
import { ActionRegistry } from '~/hooks/actions/ActionRegistry.ts';
import { ActionResult } from '~/hooks/actions/enums';
import type { Action } from '~/hooks/actions/types.ts';
import type {
  PopupMenuAnchorRect,
  PopupMenuAction,
  PopupMenuFoundationFeed,
  PopupMenuHeaderAction,
  PopupMenuPointer,
  PopupMenuProps,
  PopupMenuSelectPayload,
} from './types.ts';
import type { loader as rootLoader } from '~/root.tsx';
import { getClientFeedUrl } from '~/services/request';
import { getMergedClientContextKeys } from '~/utils/context-setter-storage.ts';
import { requestJsonData } from '~/utils/request-json-data.ts';
import { isValidUrl } from '~/utils/validations.ts';
import { useIsLoggedIn } from '~/hooks/use-is-logged-in.ts';
import { dispatchAudioPlayerCollapseExpanded } from '~/utils/audio-player.ts';
import { isRecord } from '~/services/layout/layout.ts';

export const popupMenuFoundationFeed: PopupMenuFoundationFeed = {
  id: 'popup-menu-foundation-feed',
  title: 'Contextual Actions',
  type: { value: 'action_menu' },
  entry: [
    {
      id: 'popup-menu-entry-foundation-1',
      title: 'Contextual Actions',
      subtitle: 'Actions for the selected item',
      trigger: {
        label: 'Test Item: Left click or right click',
        icon: 'more',
      },
      header: {
        titleAlignment: 'left',
      },
      closeButton: true,
      closeButtonLabel: 'Dismiss',
      extensions: {
        entry_action: [
          {
            button: {
              alias: 'add_to_queue',
            },
            dismiss_on_action: true,
            actions: [
              {
                type: 'addToQueue',
                options: {
                  itemId: 'popup-menu-entry-foundation-1',
                  itemTitle: 'Contextual Actions',
                  itemSubtitle: 'Actions for the selected item',
                  maxItems: 50,
                },
              },
            ],
          },
          {
            button: {
              alias: 'add_to_playlist',
            },
            dismiss_on_action: false,
            children: [
              {
                id: 'add-to-playlist-favorites',
                label: 'Add to Favorites Playlist',
                actions: [
                  {
                    type: 'sendCloudEvent',
                    options: {
                      url: '@{ctx/cloud_events_url}',
                      type: 'com.applicaster.collection.toggle.v1',
                      subject: 'collection_mutation',
                      data: {
                        collectionId: 'favorites-playlist',
                        itemId: 'popup-menu-entry-foundation-1',
                      },
                    },
                  },
                  {
                    type: 'refreshComponent',
                    options: {},
                  },
                ],
              },
              {
                id: 'add-to-playlist-workout',
                label: 'Add to Workout Playlist',
                actions: [
                  {
                    type: 'sendCloudEvent',
                    options: {
                      url: '@{ctx/cloud_events_url}',
                      type: 'com.applicaster.collection.toggle.v1',
                      subject: 'collection_mutation',
                      data: {
                        collectionId: 'workout-playlist',
                        itemId: 'popup-menu-entry-foundation-1',
                      },
                    },
                  },
                  {
                    type: 'refreshComponent',
                    options: {},
                  },
                ],
              },
              {
                id: 'add-to-playlist-focus',
                label: 'Add to Focus Playlist',
                actions: [
                  {
                    type: 'sendCloudEvent',
                    options: {
                      url: '@{ctx/cloud_events_url}',
                      type: 'com.applicaster.collection.toggle.v1',
                      subject: 'collection_mutation',
                      data: {
                        collectionId: 'focus-playlist',
                        itemId: 'popup-menu-entry-foundation-1',
                      },
                    },
                  },
                  {
                    type: 'refreshComponent',
                    options: {},
                  },
                ],
              },
            ],
          },
          {
            id: 'favorite',
            label: 'Favorite',
            icon: 'favorite',
            dismiss_on_action: true,
            actions: [
              {
                type: 'localStorageSet',
                options: {
                  content: {
                    user_preferences: {
                      favorite_item_id: 'popup-menu-entry-foundation-1',
                    },
                  },
                },
              },
              {
                type: 'refreshComponent',
                options: {},
              },
            ],
          },
          {
            id: 'share',
            label: 'Share',
            icon: 'share',
            href: '/share',
          },
          {
            button: {
              alias: 'sleep_timer',
            },
            dismiss_on_action: false,
            children: [
              {
                id: 'sleep-timer-15-min',
                label: '15 minutes',
                actions: [
                  {
                    type: 'localStorageSet',
                    options: {
                      content: {
                        player_preferences: {
                          sleep_timer_minutes: 15,
                        },
                      },
                    },
                  },
                ],
              },
              {
                id: 'sleep-timer-30-min',
                label: '30 minutes',
                actions: [
                  {
                    type: 'localStorageSet',
                    options: {
                      content: {
                        player_preferences: {
                          sleep_timer_minutes: 30,
                        },
                      },
                    },
                  },
                ],
              },
              {
                id: 'sleep-timer-60-min',
                label: '60 minutes',
                actions: [
                  {
                    type: 'localStorageSet',
                    options: {
                      content: {
                        player_preferences: {
                          sleep_timer_minutes: 60,
                        },
                      },
                    },
                  },
                ],
              },
            ],
          },
          {
            button: {
              alias: 'playback_speed',
            },
            dismiss_on_action: false,
            children: [
              {
                id: 'playback-speed-0-5',
                label: '0.5x',
                actions: [
                  {
                    type: 'localStorageSet',
                    options: {
                      content: {
                        player_preferences: {
                          playback_speed: 0.5,
                        },
                      },
                    },
                  },
                ],
              },
              {
                id: 'playback-speed-1',
                label: '1.0x',
                actions: [
                  {
                    type: 'localStorageSet',
                    options: {
                      content: {
                        player_preferences: {
                          playback_speed: 1,
                        },
                      },
                    },
                  },
                ],
              },
              {
                id: 'playback-speed-1-5',
                label: '1.5x',
                actions: [
                  {
                    type: 'localStorageSet',
                    options: {
                      content: {
                        player_preferences: {
                          playback_speed: 1.5,
                        },
                      },
                    },
                  },
                ],
              },
            ],
          },
          {
            button: {
              alias: 'go_to_queue',
            },
            href: '/queue',
            dismiss_on_action: true,
          },
          {
            button: {
              alias: 'collection_list',
            },
            href: '/playlist',
            dismiss_on_action: true,
          },
          {
            button: {
              alias: 'go_to_artist',
            },
            href: '/artist',
            dismiss_on_action: true,
          },
        ],
      },
    },
  ],
};

type PlaylistBottomSheetActionItem = {
  id: string;
  title: string;
  subtitle?: string;
  imageSrc?: string;
  selected?: boolean;
  actions: Action[];
  dismissOnAction: boolean;
  isCreateActionItem?: boolean;
  interactionType?: 'selection' | 'management';
};

type PlaylistBottomSheetState = {
  isOpen: boolean;
  title?: string;
  subtitle?: string;
  sectionTitle?: string;
  isSelectionMode: boolean;
  createPlaylistActionItem?: PlaylistBottomSheetActionItem;
  addActions?: Action[];
  loading: boolean;
  errorMessage?: string;
  items: PlaylistBottomSheetActionItem[];
  sourceItemsUrl?: string;
};

type PlaylistBottomSheetData = {
  items: PlaylistBottomSheetActionItem[];
  isSelectionMode: boolean;
  createPlaylistActionItem?: PlaylistBottomSheetActionItem;
  addActions?: Action[];
};

type PlaylistConfirmDialogState = {
  title: string;
  message: string;
  okButtonText: string;
  cancelButtonText: string;
};

type PlaylistRenameDialogState = {
  collectionId: string;
  name: string;
  collectOnly?: boolean;
  headerTitle?: string;
  inputLabel?: string;
  buttonLabel?: string;
  maxLength?: number;
};
type PlaylistCreateDialogState = {
  actionItem: PlaylistBottomSheetActionItem;
  title: string;
  submitButtonText: string;
  inputTitle: string;
  inputPlaceholder: string;
};

type DesktopPlaylistPopoverVariant = 'mini-player' | 'menu-trigger';
const SUPPORTED_EXTERNAL_ACTION_PROTOCOLS = new Set(['https:']);
const PLAYLIST_AUTH_ALIASES = new Set([
  'add_to_playlist',
  'add_all_to_playlist',
]);
const PLAYLIST_CLOUD_EVENT_PREFIX = 'com.applicaster.collection.';
const SAME_TRIGGER_REOPEN_GUARD_MS = 500;

/**
 * Builds a zero-size anchor rect at a pointer position.
 * @param pointer - viewport coordinates of the right-click
 * @returns the anchor rect
 */
function getPointerAnchorRect(pointer: PopupMenuPointer): PopupMenuAnchorRect {
  return {
    top: pointer.y,
    right: pointer.x,
    bottom: pointer.y,
    left: pointer.x,
    width: 0,
    height: 0,
  };
}
const CREATE_PLAYLIST_BUTTON_ALIASES = new Set([
  'create_playlist',
  'create_new_playlist',
]);

function getStringValue(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
  }

  return undefined;
}

function normalizeSafeActionTarget(target?: string): string | undefined {
  if (!target || typeof target !== 'string') {
    return undefined;
  }

  const normalizedTarget = target.trim();

  if (!normalizedTarget) {
    return undefined;
  }

  if (normalizedTarget.startsWith('/')) {
    if (normalizedTarget.startsWith('//')) {
      return undefined;
    }

    return normalizedTarget;
  }

  if (!isValidUrl(normalizedTarget)) {
    return undefined;
  }

  const parsedUrl = new URL(normalizedTarget);

  if (!SUPPORTED_EXTERNAL_ACTION_PROTOCOLS.has(parsedUrl.protocol)) {
    return undefined;
  }

  return parsedUrl.toString();
}

function getReadableAlias(alias?: string): string | undefined {
  if (!alias) return undefined;

  return alias
    .split('_')
    .map((part) =>
      part ? `${part[0]?.toUpperCase() || ''}${part.slice(1)}` : ''
    )
    .join(' ')
    .trim();
}

function getEntryImageSrc(
  entryItem: Record<string, unknown>
): string | undefined {
  const directImage = getStringValue(entryItem.image, entryItem.imageSrc);

  if (directImage) {
    return directImage;
  }

  const mediaGroups = Array.isArray(entryItem.media_group)
    ? entryItem.media_group
    : [];

  for (const mediaGroup of mediaGroups) {
    if (!isRecord(mediaGroup)) {
      continue;
    }

    const mediaItems = Array.isArray(mediaGroup.media_item)
      ? mediaGroup.media_item
      : [];

    for (const mediaItem of mediaItems) {
      if (!isRecord(mediaItem)) {
        continue;
      }

      if (mediaItem.key === 'image_base') {
        const imageBaseSrc = getStringValue(mediaItem.src);

        if (imageBaseSrc) {
          return imageBaseSrc;
        }
      }
    }

    for (const mediaItem of mediaItems) {
      if (!isRecord(mediaItem)) {
        continue;
      }

      const fallbackSrc = getStringValue(mediaItem.src);

      if (fallbackSrc) {
        return fallbackSrc;
      }
    }
  }

  return undefined;
}

function normalizeActionList(source: unknown): Action[] {
  if (!Array.isArray(source)) {
    return [];
  }

  return source.filter((value): value is Action => isRecord(value));
}

function getFeedBehavior(
  feed: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (isRecord(feed.behavior)) {
    return feed.behavior;
  }

  const extensions = isRecord(feed.extensions) ? feed.extensions : undefined;
  const behavior = extensions?.behavior;

  return isRecord(behavior) ? behavior : undefined;
}

function getSelectedIdSet(feed: Record<string, unknown>): Set<string> {
  const behavior = getFeedBehavior(feed);
  const extensions = isRecord(feed.extensions) ? feed.extensions : undefined;
  const currentSelection =
    behavior?.current_selection ??
    extensions?.current_selection ??
    feed.current_selection;
  const currentSelectionSource = Array.isArray(currentSelection)
    ? currentSelection
    : currentSelection !== undefined && currentSelection !== null
    ? [currentSelection]
    : [];

  const selectedIds = currentSelectionSource
    .map((selectionItem) => {
      if (
        typeof selectionItem === 'string' ||
        typeof selectionItem === 'number'
      ) {
        return String(selectionItem);
      }

      if (!isRecord(selectionItem)) {
        return undefined;
      }

      const rawId =
        selectionItem.id ??
        selectionItem.identifier ??
        selectionItem.collectionId ??
        selectionItem.collection_id;

      return rawId !== undefined && rawId !== null ? String(rawId) : undefined;
    })
    .filter((value): value is string => Boolean(value));

  return new Set(selectedIds);
}

function isSelectionFeed(feed: Record<string, unknown>): boolean {
  const behavior = getFeedBehavior(feed);
  const extensions = isRecord(feed.extensions) ? feed.extensions : undefined;
  const role = getStringValue(feed.role, extensions?.role);
  const selectMode = getStringValue(behavior?.select_mode);
  const hasCurrentSelection = Array.isArray(behavior?.current_selection);

  if (role === 'collection_selector' || role === 'preference_editor') {
    return true;
  }

  return Boolean(selectMode) || hasCurrentSelection;
}

function getEntrySubtitle(
  entryItem: Record<string, unknown>
): string | undefined {
  const directSubtitle = getStringValue(entryItem.subtitle, entryItem.summary);

  if (directSubtitle) {
    return directSubtitle;
  }

  const countValue = entryItem.count;

  if (typeof countValue === 'number' && Number.isFinite(countValue)) {
    return `${countValue} items`;
  }

  if (typeof countValue === 'string' && countValue.trim()) {
    return `${countValue.trim()} items`;
  }

  return undefined;
}

function isCreatePlaylistAction(action: Action): boolean {
  if (!action || action.type !== 'sendCloudEvent') {
    return false;
  }

  const options = isRecord(action.options) ? action.options : undefined;
  const cloudEventType = getStringValue(options?.type);

  return cloudEventType === 'com.applicaster.collection.create.v1';
}

function isCreatePlaylistAlias(value: string): boolean {
  return CREATE_PLAYLIST_BUTTON_ALIASES.has(value.trim().toLowerCase());
}

function hasCreatePlaylistIntentMarker(
  entryAction: Record<string, unknown>
): boolean {
  const button = isRecord(entryAction.button) ? entryAction.button : undefined;
  const alias = getStringValue(
    entryAction.alias,
    button?.alias,
    entryAction.action_alias
  );

  if (!alias) {
    return false;
  }

  return isCreatePlaylistAlias(alias);
}

function splitCreatePlaylistActionItem(
  items: PlaylistBottomSheetActionItem[]
): {
  itemsWithoutCreateAction: PlaylistBottomSheetActionItem[];
  createPlaylistActionItem?: PlaylistBottomSheetActionItem;
} {
  const createCandidates = items.filter((item) => {
    return Boolean(item.isCreateActionItem);
  });

  if (createCandidates.length === 0) {
    return {
      itemsWithoutCreateAction: items,
      createPlaylistActionItem: undefined,
    };
  }

  const createPlaylistActionItem =
    createCandidates.find((item) => {
      return item.actions.some((action) => isCreatePlaylistAction(action));
    }) || createCandidates[0];

  const createCandidateIds = new Set(createCandidates.map((item) => item.id));

  return {
    itemsWithoutCreateAction: items.filter(
      (item) => !createCandidateIds.has(item.id)
    ),
    createPlaylistActionItem,
  };
}

function getFeedAddActions(
  feed: Record<string, unknown>
): Action[] | undefined {
  const extensions = isRecord(feed.extensions) ? feed.extensions : undefined;
  const dynamicCollectionOptions = extensions?.dynamic_collection_options;
  const dynamicOptions = isRecord(dynamicCollectionOptions)
    ? dynamicCollectionOptions
    : undefined;
  const operationsValue = dynamicOptions?.operations;
  const operations = Array.isArray(operationsValue)
    ? operationsValue
    : typeof operationsValue === 'string'
    ? [operationsValue]
    : [];
  const canAdd = operations
    .filter((operation): operation is string => typeof operation === 'string')
    .map((operation) => operation.toLowerCase())
    .includes('add');

  if (!canAdd || !dynamicOptions) {
    return undefined;
  }

  const events = isRecord(dynamicOptions.events)
    ? dynamicOptions.events
    : undefined;
  const addActions = normalizeActionList(events?.add);

  return addActions.length > 0 ? addActions : undefined;
}

function normalizeBottomSheetData(feed: unknown): PlaylistBottomSheetData {
  if (!isRecord(feed)) {
    return {
      items: [],
      isSelectionMode: false,
      createPlaylistActionItem: undefined,
      addActions: undefined,
    };
  }

  const entry = Array.isArray(feed.entry) ? feed.entry : [];
  const selectedIdSet = getSelectedIdSet(feed);
  const isSelectionMode = isSelectionFeed(feed);

  const items: PlaylistBottomSheetActionItem[] = entry.flatMap(
    (entryItem, entryIndex): PlaylistBottomSheetActionItem[] => {
      if (!isRecord(entryItem)) return [];

      const entryId = getStringValue(entryItem.id, entryItem.identifier);
      const entryTitle =
        getStringValue(entryItem.title, entryItem.name, entryItem.label) ||
        `Playlist ${entryIndex + 1}`;
      const entrySubtitle = getEntrySubtitle(entryItem);
      const imageSrc = getEntryImageSrc(entryItem);
      const selected = Boolean(entryId && selectedIdSet.has(entryId));

      const entryExtensionsSource = entryItem.extensions;
      const entryExtensions = isRecord(entryExtensionsSource)
        ? entryExtensionsSource
        : undefined;

      const entryActionsSource = entryExtensions?.entry_action;
      const entryActions = Array.isArray(entryActionsSource)
        ? entryActionsSource
        : [];

      const normalizedEntryActionItems: PlaylistBottomSheetActionItem[] =
        entryActions.flatMap(
          (entryAction, actionIndex): PlaylistBottomSheetActionItem[] => {
            if (!isRecord(entryAction)) return [];

            const button = isRecord(entryAction.button)
              ? entryAction.button
              : undefined;
            const alias = getStringValue(button?.alias);
            const label =
              getStringValue(entryAction.label, button?.label) ||
              entryTitle ||
              getReadableAlias(alias) ||
              `Action ${entryIndex + 1}.${actionIndex + 1}`;

            const actions = normalizeActionList(entryAction.actions);
            const hasCreatePlaylistAction = actions.some((action) => {
              return isCreatePlaylistAction(action);
            });
            const hasCreatePlaylistMarker =
              hasCreatePlaylistIntentMarker(entryAction);
            const isCreateActionItem =
              hasCreatePlaylistAction || hasCreatePlaylistMarker;

            if (!actions.length) {
              return [];
            }

            return [
              {
                id:
                  getStringValue(entryAction.id, button?.id) ||
                  `${entryIndex}-${actionIndex}-${label}`,
                title: label,
                subtitle: entrySubtitle,
                imageSrc,
                selected,
                actions,
                isCreateActionItem,
                interactionType: 'management',
                dismissOnAction:
                  typeof entryAction.dismiss_on_action === 'boolean'
                    ? entryAction.dismiss_on_action
                    : true,
              },
            ];
          }
        );

      const tapActionsSource =
        (isRecord(entryItem.tap_actions)
          ? entryItem.tap_actions.actions
          : undefined) ||
        (isRecord(entryExtensions?.tap_actions)
          ? entryExtensions?.tap_actions.actions
          : undefined) ||
        entryItem.actions;

      const tapActions = normalizeActionList(tapActionsSource);

      const hasRemoveIntent = tapActions.some((action) => {
        const actionType = getStringValue(action.type);
        const actionOptions = isRecord(action.options) ? action.options : {};
        const actionSubject = getStringValue(actionOptions.subject);
        return (
          actionType?.includes('remove') || actionSubject?.includes('remove')
        );
      });

      const effectiveSelected = Boolean(selected || hasRemoveIntent);

      if (tapActions.length > 0) {
        const id = entryId || `${entryIndex}-tap`;

        return [
          ...normalizedEntryActionItems,
          {
            id,
            title: entryTitle,
            subtitle: entrySubtitle,
            imageSrc,
            selected: effectiveSelected,
            actions: tapActions,
            interactionType: 'selection',
            dismissOnAction: Boolean(
              isRecord(entryItem.tap_actions) &&
                entryItem.tap_actions.dismiss_on_action
            ),
          },
        ];
      }

      return normalizedEntryActionItems;
    }
  );

  const { itemsWithoutCreateAction, createPlaylistActionItem } =
    splitCreatePlaylistActionItem(items);

  return {
    items: itemsWithoutCreateAction,
    isSelectionMode,
    createPlaylistActionItem,
    addActions: getFeedAddActions(feed),
  };
}

function renderAction({
  action,
  actionPath,
  getIconSrc,
  isDesktopLargeDesktop,
  isDesktopActionExpanded,
  onDesktopActionHover,
  onNestedActionIntent,
  onSelect,
}: {
  action: PopupMenuAction;
  actionPath: string[];
  getIconSrc: (icon?: string, action?: PopupMenuAction) => string | undefined;
  isDesktopLargeDesktop: boolean;
  isDesktopActionExpanded: (path: string[]) => boolean;
  onDesktopActionHover: (params: {
    action: PopupMenuAction;
    path: string[];
  }) => void;
  onNestedActionIntent: (params: {
    action: PopupMenuAction;
    path: string[];
  }) => boolean;
  onSelect: (payload: PopupMenuSelectPayload) => void;
}): JSX.Element {
  const hasChildren = Boolean(action.children?.length);
  const normalizedAlias = action.alias?.trim().toLowerCase();
  const showsPlaylistNavigationIndicator =
    normalizedAlias === 'add_to_playlist' ||
    normalizedAlias === 'add_all_to_playlist';
  const shouldRenderTrailingIndicator =
    hasChildren || showsPlaylistNavigationIndicator;
  const iconSrc = getIconSrc(action.icon, action);
  const safeActionTarget = normalizeSafeActionTarget(action.url || action.href);
  const nestedIndicatorIconSrc = getIconSrc('next', action);
  const isExpanded = isDesktopActionExpanded(actionPath);

  const content = (
    <>
      {iconSrc ? (
        <span className="popup-menu-item-leading-icon-classes" aria-hidden>
          <img src={iconSrc} alt="" className="h-full w-full object-contain" />
        </span>
      ) : null}

      <span className="popup-menu-item-label-classes">{action.label}</span>

      {shouldRenderTrailingIndicator ? (
        <span className="popup-menu-item-trailing-icon-classes" aria-hidden>
          {nestedIndicatorIconSrc ? (
            <img
              src={nestedIndicatorIconSrc}
              alt=""
              className="h-full w-full object-contain"
            />
          ) : (
            '>'
          )}
        </span>
      ) : null}
    </>
  );

  return (
    <div key={action.id} className="popup-menu-action-wrapper-classes">
      <div className="popup-menu-item-classes">
        {safeActionTarget ? (
          <a
            href={safeActionTarget}
            className="popup-menu-action-control-classes"
            role="menuitem"
            data-popup-has-children={hasChildren ? 'true' : 'false'}
            onClick={(event) => {
              const handled = onNestedActionIntent({
                action,
                path: actionPath,
              });

              if (handled) {
                event.preventDefault();
                return;
              }

              if (Array.isArray(action.actions) && action.actions.length) {
                event.preventDefault();
              }
              const rect = event.currentTarget.getBoundingClientRect();

              onSelect({
                action,
                triggerRect: {
                  top: rect.top,
                  right: rect.right,
                  bottom: rect.bottom,
                  left: rect.left,
                  width: rect.width,
                  height: rect.height,
                },
              });
            }}
            onMouseEnter={() => {
              onDesktopActionHover({
                action,
                path: actionPath,
              });
            }}
            onFocus={() => {
              onDesktopActionHover({
                action,
                path: actionPath,
              });
            }}
          >
            {content}
          </a>
        ) : (
          <button
            type="button"
            className="popup-menu-action-control-classes"
            role="menuitem"
            data-popup-has-children={hasChildren ? 'true' : 'false'}
            onClick={(event) => {
              const handled = onNestedActionIntent({
                action,
                path: actionPath,
              });

              if (handled) return;

              const rect = event.currentTarget.getBoundingClientRect();

              onSelect({
                action,
                triggerRect: {
                  top: rect.top,
                  right: rect.right,
                  bottom: rect.bottom,
                  left: rect.left,
                  width: rect.width,
                  height: rect.height,
                },
              });
            }}
            onMouseEnter={() => {
              onDesktopActionHover({
                action,
                path: actionPath,
              });
            }}
            onFocus={() => {
              onDesktopActionHover({
                action,
                path: actionPath,
              });
            }}
          >
            {content}
          </button>
        )}
      </div>

      {hasChildren && isDesktopLargeDesktop && isExpanded ? (
        <div className="popup-menu-children-classes" role="menu">
          {action.children?.map((childAction) =>
            renderAction({
              action: childAction,
              actionPath: [...actionPath, childAction.id],
              getIconSrc,
              isDesktopLargeDesktop,
              isDesktopActionExpanded,
              onDesktopActionHover,
              onNestedActionIntent,
              onSelect,
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

function findActionByAlias({
  actions,
  alias,
}: {
  actions: PopupMenuAction[];
  alias: string;
}): PopupMenuAction | null {
  for (const action of actions) {
    if (action.alias?.toLowerCase() === alias) {
      return action;
    }

    if (Array.isArray(action.children) && action.children.length > 0) {
      const nestedMatch = findActionByAlias({
        actions: action.children,
        alias,
      });

      if (nestedMatch) {
        return nestedMatch;
      }
    }
  }

  return null;
}

function isPlaylistCloudEventMutation(action: Action): boolean {
  if (
    !action ||
    (action.type !== 'sendCloudEvent' && action.type !== 'postForm')
  ) {
    return false;
  }

  const options = isRecord(action.options) ? action.options : undefined;
  const cloudEventType = getStringValue(options?.type)?.trim().toLowerCase();

  if (!cloudEventType) {
    return false;
  }

  return cloudEventType.startsWith(PLAYLIST_CLOUD_EVENT_PREFIX);
}

function isPlaylistAuthRequiredAction(action: PopupMenuAction): boolean {
  const normalizedAlias = action.alias?.trim().toLowerCase();

  if (normalizedAlias && PLAYLIST_AUTH_ALIASES.has(normalizedAlias)) {
    return true;
  }

  if (
    Array.isArray(action.actions) &&
    action.actions.some(isPlaylistCloudEventMutation)
  ) {
    return true;
  }

  if (Array.isArray(action.children) && action.children.length > 0) {
    return action.children.some((childAction) => {
      return isPlaylistAuthRequiredAction(childAction);
    });
  }

  return false;
}

export default function PopupMenu({
  uiComponent,
  feed = popupMenuFoundationFeed,
  onActionSelect,
  hideTriggerButton,
  rootZIndex,
  favoriteAction,
  openRequest,
  isLoggedIn: knownIsLoggedIn,
}: PopupMenuProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn: loadedIsLoggedIn } = useIsLoggedIn();
  const isLoggedIn: boolean | null = knownIsLoggedIn ?? loadedIsLoggedIn;
  const rootLoaderData = useRouteLoaderData<typeof rootLoader>('root');

  const rootRef = useRef<HTMLElement | null>(null);
  const playlistLayerRef = useRef<HTMLElement | null>(null);
  const confirmDialogLayerRef = useRef<HTMLElement | null>(null);
  const renameDialogLayerRef = useRef<HTMLElement | null>(null);
  const renameDialogInputRef = useRef<HTMLInputElement | null>(null);
  const createDialogLayerRef = useRef<HTMLElement | null>(null);
  const createDialogInputRef = useRef<HTMLInputElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const activeMenuTriggerRef = useRef<HTMLElement | null>(null);
  const playlistPopoverTriggerRef = useRef<HTMLElement | null>(null);
  const lastMenuCloseRef = useRef<{ target: Node | null; at: number }>({
    target: null,
    at: 0,
  });
  const confirmResolverRef = useRef<((confirmed: boolean) => void) | null>(
    null
  );
  const renameResolverRef = useRef<((result: boolean | string) => void) | null>(
    null
  );

  const [playlistBottomSheet, setPlaylistBottomSheet] =
    useState<PlaylistBottomSheetState>({
      isOpen: false,
      isSelectionMode: false,
      createPlaylistActionItem: undefined,
      loading: false,
      items: [],
    });
  const [confirmDialogState, setConfirmDialogState] =
    useState<PlaylistConfirmDialogState | null>(null);
  const [renameDialogState, setRenameDialogState] =
    useState<PlaylistRenameDialogState | null>(null);
  const [createDialogState, setCreateDialogState] =
    useState<PlaylistCreateDialogState | null>(null);
  const [renameInputValue, setRenameInputValue] = useState<string>('');
  const [createInputValue, setCreateInputValue] = useState<string>('');
  const [playlistSearchQuery, setPlaylistSearchQuery] = useState<string>('');
  const [playlistStagedSelectionById, setPlaylistStagedSelectionById] =
    useState<Record<string, boolean>>({});
  const [playlistStagedDirtyIds, setPlaylistStagedDirtyIds] = useState<
    string[]
  >([]);
  const [playlistPopoverAnchorRect, setPlaylistPopoverAnchorRect] = useState<
    PopupMenuAnchorRect | undefined
  >(undefined);
  const [desktopPlaylistPopoverVariant, setDesktopPlaylistPopoverVariant] =
    useState<DesktopPlaylistPopoverVariant | null>(null);
  const [activeTitle, setActiveTitle] = useState<string | null>(null);

  const { entry, getIconSrc, componentStyle } = usePopupMenuData({
    feed,
    uiComponentStyles: uiComponent?.styles as
      | Record<string, unknown>
      | undefined,
    playlistConfig: rootLoaderData?.playlistConfig as
      | Record<string, unknown>
      | undefined,
    queueConfig: rootLoaderData?.queueWebConfig as
      | Record<string, unknown>
      | undefined,
    isThemeV2: rootLoaderData?.isThemeV2,
  });

  const triggerIconSrc = getIconSrc(entry.triggerIcon);

  const {
    isDesktopLargeDesktop,
    isOpen,
    openFromElement,
    rootStyle,
    setIsOpen,
    surfaceStyle,
  } = usePopupMenuPositioning({
    entry,
    surfaceRef,
  });

  const computedRootStyle = useMemo(() => {
    return {
      ...rootStyle,
      zIndex: rootZIndex ?? rootStyle.zIndex,
    };
  }, [rootStyle, rootZIndex]);

  const {
    canGoBack,
    currentMobileLevelLabel,
    goBack,
    handleActionSelectionIntent,
    handleDesktopActionHover,
    isDesktopActionExpanded,
    resetNestedNavigation,
    visibleSections,
  } = useNestedNavigation({
    entry,
    isDesktopLargeDesktop,
    isOpen,
  });

  const recordMenuDismissal = useCallback((eventTarget: Node): void => {
    const activeMenuTrigger: HTMLElement | null = activeMenuTriggerRef.current;

    lastMenuCloseRef.current = {
      target: activeMenuTrigger?.contains(eventTarget)
        ? activeMenuTrigger
        : null,
      at: Date.now(),
    };
  }, []);

  const { closeMenu } = usePopupMenuDismissal({
    activeTriggerRef: activeMenuTriggerRef,
    isOpen,
    setIsOpen,
    triggerRef,
  });

  const overlayMode: PopupMenuOverlayMode = getPopupMenuOverlayMode({
    isDesktop: isDesktopLargeDesktop,
  });
  const menuOverlayInsideRefs: OutsidePressRefs = [
    rootRef,
    playlistLayerRef,
    confirmDialogLayerRef,
    renameDialogLayerRef,
    createDialogLayerRef,
  ];
  const playlistOverlayInsideRefs: OutsidePressRefs = [
    ...menuOverlayInsideRefs,
    playlistPopoverTriggerRef,
  ];

  const closeMenuAndReset = useCallback((): void => {
    resetNestedNavigation();

    if (!playlistBottomSheet.isOpen) {
      setPlaylistPopoverAnchorRect(undefined);
      setDesktopPlaylistPopoverVariant(null);
    }

    closeMenu();
    activeMenuTriggerRef.current = null;
  }, [closeMenu, playlistBottomSheet.isOpen, resetNestedNavigation]);

  useEffect(
    () => () => {
      // Dismiss the confirm dialog if it is open
      confirmResolverRef.current?.(false);
      confirmResolverRef.current = null;

      // Dismiss the rename dialog if it is open
      renameResolverRef.current?.(false);
      renameResolverRef.current = null;
    },
    []
  );

  const dismissMenu = (pressTarget: Node): void => {
    recordMenuDismissal(pressTarget);
    closeMenuAndReset();
  };

  const playlistLocalizations = useMemo(() => {
    const playlistConfig = rootLoaderData?.playlistConfig as
      | Record<string, unknown>
      | undefined;
    const localizationsSource = playlistConfig?.localizations;
    const localizations = isRecord(localizationsSource)
      ? localizationsSource
      : undefined;

    return {
      createNewPlaylistButtonLabel:
        getStringValue(localizations?.create_new_playlist_button_label) ||
        'Create New Playlist',
      createNewPlaylistTitle:
        getStringValue(localizations?.create_new_playlist_title) ||
        'Create New Playlist',
      createPlaylistButtonLabel:
        getStringValue(localizations?.create_playlist_button_label) ||
        'Create Playlist',
      playlistNameInputTitle:
        getStringValue(localizations?.playlist_name_input_title) ||
        'Name your playlist',
      playlistNameHint:
        getStringValue(localizations?.playlist_name_hint) || 'Playlist Name',
      searchPlaylistsFieldHint:
        getStringValue(localizations?.search_playlists_field_hint) ||
        'Find a playlist...',
      recentPlaylistsSectionTitle:
        getStringValue(localizations?.recent_playlists_section_title) ||
        'Recent',
    };
  }, [rootLoaderData?.playlistConfig]);

  const resolveCloudEventActions = useCallback(
    (actions: Action[]): Action[] => {
      return addInflateDataToCloudEventActions(actions);
    },
    []
  );

  const closePlaylistBottomSheet = useCallback((): void => {
    setPlaylistSearchQuery('');
    setPlaylistStagedSelectionById({});
    setPlaylistStagedDirtyIds([]);
    setCreateDialogState(null);
    setCreateInputValue('');
    setPlaylistPopoverAnchorRect(undefined);
    setDesktopPlaylistPopoverVariant(null);
    playlistPopoverTriggerRef.current = null;
    setPlaylistBottomSheet({
      isOpen: false,
      isSelectionMode: false,
      createPlaylistActionItem: undefined,
      loading: false,
      items: [],
    });
  }, []);

  const initializeStagedSelection = useCallback(
    (items: PlaylistBottomSheetActionItem[]): void => {
      const nextSelectionById = items.reduce<Record<string, boolean>>(
        (accumulator, item) => {
          accumulator[item.id] = Boolean(item.selected);
          return accumulator;
        },
        {}
      );

      setPlaylistStagedSelectionById(nextSelectionById);
      setPlaylistStagedDirtyIds([]);
    },
    []
  );

  const loadPlaylistBottomSheetData = useCallback(
    async (
      itemsUrl?: string,
      options?: { forceFresh?: boolean }
    ): Promise<PlaylistBottomSheetData> => {
      if (!itemsUrl)
        return {
          items: [],
          isSelectionMode: false,
        };

      const mergedClientContextKeys = getMergedClientContextKeys();

      const isForceFresh = Boolean(options?.forceFresh);

      const clientFeedUrl = `/${getClientFeedUrl(
        itemsUrl,
        Object.keys(mergedClientContextKeys).length > 0
          ? mergedClientContextKeys
          : undefined
      )}`;

      let requestUrl = clientFeedUrl;
      if (isForceFresh && typeof window !== 'undefined') {
        const urlObj = new URL(clientFeedUrl, window.location.origin);
        urlObj.searchParams.set('force-fresh', 'true');
        requestUrl = `${urlObj.pathname}${urlObj.search}`;
      }

      const feed = await requestJsonData({
        url: requestUrl,
        options: isForceFresh ? { cache: 'no-store' } : undefined,
      });

      const normalizedData = normalizeBottomSheetData(feed);

      return normalizedData;
    },
    []
  );

  const openPlaylistBottomSheet = useCallback(
    async (options: {
      header?: { title?: string; subtitle?: string };
      content?: { title?: string; itemsUrl?: string; items?: unknown[] };
    }): Promise<boolean> => {
      const header = isRecord(options.header) ? options.header : {};
      const content = isRecord(options.content) ? options.content : {};
      const sourceItems = Array.isArray(content.items) ? content.items : [];
      const staticItems = sourceItems.flatMap((item, index) => {
        if (!isRecord(item)) return [];

        const label =
          getStringValue(item.label, item.title) || `Item ${index + 1}`;
        const actions = Array.isArray(item.actions)
          ? item.actions.filter((value): value is Action => isRecord(value))
          : [];

        if (!actions.length) return [];

        return [
          {
            id: `static-${index}`,
            title: label,
            actions,
            dismissOnAction:
              typeof item.dismiss_on_action === 'boolean'
                ? item.dismiss_on_action
                : false,
          } as PlaylistBottomSheetActionItem,
        ];
      });

      const sourceItemsUrl = getStringValue(content.itemsUrl);

      if (!sourceItemsUrl && staticItems.length === 0) {
        closePlaylistBottomSheet();
        return false;
      }

      const isRemoteFeed = Boolean(
        sourceItemsUrl &&
          (sourceItemsUrl.startsWith('http://') ||
            sourceItemsUrl.startsWith('https://'))
      );

      if (sourceItemsUrl && staticItems.length === 0) {
        try {
          const remoteData = await loadPlaylistBottomSheetData(sourceItemsUrl, {
            forceFresh: isRemoteFeed,
          });

          const remoteItems = remoteData.items;
          const resolvedCreatePlaylistActionItem =
            remoteData.createPlaylistActionItem;

          if (
            remoteItems.length === 0 &&
            !resolvedCreatePlaylistActionItem &&
            !remoteData.addActions?.length
          ) {
            closePlaylistBottomSheet();
            return false;
          }

          setPlaylistBottomSheet({
            isOpen: true,
            title: getStringValue(header.title),
            subtitle: getStringValue(header.subtitle),
            sectionTitle: getStringValue(content.title),
            isSelectionMode: remoteData.isSelectionMode,
            createPlaylistActionItem: resolvedCreatePlaylistActionItem,
            addActions: remoteData.addActions,
            loading: false,
            items: remoteItems,
            sourceItemsUrl,
            errorMessage: undefined,
          });
          setPlaylistSearchQuery('');
          initializeStagedSelection(remoteItems);

          return true;
        } catch (error) {
          closePlaylistBottomSheet();
          return false;
        }
      }

      setPlaylistBottomSheet({
        isOpen: true,
        title: getStringValue(header.title),
        subtitle: getStringValue(header.subtitle),
        sectionTitle: getStringValue(content.title),
        isSelectionMode: false,
        createPlaylistActionItem: undefined,
        loading: Boolean(sourceItemsUrl),
        items: staticItems,
        sourceItemsUrl,
      });
      setPlaylistSearchQuery('');

      if (!sourceItemsUrl) {
        return true;
      }

      try {
        const remoteData = await loadPlaylistBottomSheetData(sourceItemsUrl, {
          forceFresh: isRemoteFeed,
        });
        const remoteItems = remoteData.items;
        const resolvedCreatePlaylistActionItem =
          remoteData.createPlaylistActionItem;

        if (
          remoteItems.length === 0 &&
          staticItems.length === 0 &&
          !resolvedCreatePlaylistActionItem &&
          !remoteData.addActions?.length
        ) {
          closePlaylistBottomSheet();
          return false;
        }

        setPlaylistBottomSheet((prevState) => ({
          ...prevState,
          isSelectionMode: remoteData.isSelectionMode,
          createPlaylistActionItem: resolvedCreatePlaylistActionItem,
          addActions: remoteData.addActions,
          loading: false,
          items: remoteItems,
          errorMessage: undefined,
        }));
        initializeStagedSelection(remoteItems);

        return true;
      } catch (error) {
        closePlaylistBottomSheet();

        return false;
      }
    },
    [
      closePlaylistBottomSheet,
      initializeStagedSelection,
      loadPlaylistBottomSheetData,
    ]
  );

  const confirmPlaylistDialog = useCallback(
    async (options: {
      title?: string;
      message?: string;
      okButtonText?: string;
      cancelButtonText?: string;
    }): Promise<boolean> => {
      return new Promise((resolve) => {
        confirmResolverRef.current = resolve;
        setConfirmDialogState({
          title: options.title || 'Confirm',
          message: options.message || 'Are you sure?',
          okButtonText: options.okButtonText || 'OK',
          cancelButtonText: options.cancelButtonText || 'Cancel',
        });
      });
    },
    []
  );

  const editPlaylistCollectionName = useCallback(
    async (options: {
      collectionId: string;
      name?: string;
    }): Promise<boolean> => {
      return new Promise((resolve) => {
        renameResolverRef.current = (result) => {
          resolve(result === true);
        };
        setRenameDialogState({
          collectionId: options.collectionId,
          name: options.name || '',
        });
        setRenameInputValue(options.name || '');
      });
    },
    []
  );

  const promptPlaylistTextInput = useCallback(
    async (options: {
      defaultValue?: string;
      headerTitle?: string;
      inputLabel?: string;
      buttonLabel?: string;
      maxLength?: number;
    }): Promise<string | false> => {
      return new Promise((resolve) => {
        const defaultValue = options.defaultValue || '';

        renameResolverRef.current = (result) => {
          resolve(typeof result === 'string' ? result : false);
        };
        setRenameDialogState({
          collectionId: '',
          name: defaultValue,
          collectOnly: true,
          headerTitle: options.headerTitle,
          inputLabel: options.inputLabel,
          buttonLabel: options.buttonLabel,
          maxLength: options.maxLength,
        });
        setRenameInputValue(defaultValue);
      });
    },
    []
  );

  const executeBottomSheetActionItem = useCallback(
    async (
      item: PlaylistBottomSheetActionItem,
      actionsOverride?: Action[]
    ): Promise<void> => {
      const normalizedActions = resolveCloudEventActions(
        actionsOverride || item.actions
      );
      const refreshFeedEntry = playlistBottomSheet.sourceItemsUrl
        ? { _parentFeedPath: playlistBottomSheet.sourceItemsUrl }
        : undefined;

      const executionResult = await ActionRegistry.getInstance().execute(
        normalizedActions,
        {
          context: {
            uiComponent,
            entry,
            feedEntry: refreshFeedEntry,
            openPlaylistBottomSheet,
            closePlaylistBottomSheet,
            closeMenuAndReset,
            confirmPlaylistDialog,
            editPlaylistCollectionName,
            promptPlaylistTextInput,
          },
        }
      );

      if (executionResult.final === ActionResult.Cancel) {
        return;
      }

      if (executionResult.final === ActionResult.Error) {
        return;
      }

      if (item.dismissOnAction) {
        closePlaylistBottomSheet();
        closeMenuAndReset();
        return;
      }

      if (!playlistBottomSheet.sourceItemsUrl) {
        return;
      }

      const refreshedData = await loadPlaylistBottomSheetData(
        playlistBottomSheet.sourceItemsUrl,
        { forceFresh: true }
      );
      const refreshedItems = refreshedData.items;
      const resolvedCreatePlaylistActionItem =
        refreshedData.createPlaylistActionItem;

      if (
        refreshedItems.length === 0 &&
        !resolvedCreatePlaylistActionItem &&
        !refreshedData.addActions?.length
      ) {
        closePlaylistBottomSheet();
        closeMenuAndReset();
        return;
      }

      setPlaylistBottomSheet((prevState) => ({
        ...prevState,
        isSelectionMode: refreshedData.isSelectionMode,
        createPlaylistActionItem: resolvedCreatePlaylistActionItem,
        addActions: refreshedData.addActions,
        items: refreshedItems,
      }));
      setPlaylistStagedSelectionById((prevState) => {
        const next = refreshedItems.reduce<Record<string, boolean>>(
          (accumulator, refreshedItem) => {
            accumulator[refreshedItem.id] =
              Object.prototype.hasOwnProperty.call(prevState, refreshedItem.id)
                ? prevState[refreshedItem.id]
                : Boolean(refreshedItem.selected);
            return accumulator;
          },
          {}
        );
        return next;
      });
    },
    [
      closeMenuAndReset,
      closePlaylistBottomSheet,
      confirmPlaylistDialog,
      editPlaylistCollectionName,
      promptPlaylistTextInput,
      entry,
      loadPlaylistBottomSheetData,
      openPlaylistBottomSheet,
      playlistBottomSheet.sourceItemsUrl,
      resolveCloudEventActions,
      uiComponent,
    ]
  );

  useEffect(() => {
    const shouldHandleEscape =
      playlistBottomSheet.isOpen ||
      Boolean(confirmDialogState) ||
      Boolean(renameDialogState) ||
      Boolean(createDialogState);

    if (!shouldHandleEscape) {
      return;
    }

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();

      if (createDialogState) {
        setCreateDialogState(null);
        setCreateInputValue('');
        return;
      }

      if (renameDialogState) {
        renameResolverRef.current?.(false);
        renameResolverRef.current = null;
        setRenameDialogState(null);
        return;
      }

      if (confirmDialogState) {
        confirmResolverRef.current?.(false);
        confirmResolverRef.current = null;
        setConfirmDialogState(null);
        return;
      }

      closePlaylistBottomSheet();
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [
    closePlaylistBottomSheet,
    createDialogState,
    confirmDialogState,
    playlistBottomSheet.isOpen,
    renameDialogState,
  ]);

  useEffect(() => {
    if (!renameDialogState) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      renameDialogInputRef.current?.focus();
      renameDialogInputRef.current?.select();
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [renameDialogState]);

  useEffect(() => {
    if (!createDialogState) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      createDialogInputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [createDialogState]);

  useEffect(() => {
    const registry = ActionRegistry.getInstance();

    const unregisterPlaylistActions = registerPlaylistPopupActions({
      register: (type, handler) => registry.register(type, handler),
      registrationKey: registry,
    });

    return () => {
      unregisterPlaylistActions();
    };
  }, []);

  const { handleMenuKeyDown } = useKeyboardInteraction({
    canNavigateBack: canGoBack,
    closeMenu: closeMenuAndReset,
    isOpen,
    navigateBack: goBack,
    surfaceRef,
  });

  const redirectToLoginWithRedirectBack = useCallback((): void => {
    closePlaylistBottomSheet();
    closeMenuAndReset();
    dispatchAudioPlayerCollapseExpanded();

    const redirectBack = `${location.pathname}${location.search}`;

    navigate(`/login?redirectBack=${encodeURIComponent(redirectBack)}`);
  }, [
    closeMenuAndReset,
    closePlaylistBottomSheet,
    location.pathname,
    location.search,
    navigate,
  ]);

  const handleActionSelect = async (payload: PopupMenuSelectPayload) => {
    if (!isLoggedIn && isPlaylistAuthRequiredAction(payload.action)) {
      redirectToLoginWithRedirectBack();
      return;
    }

    const actions = payload.action.actions;
    const hasOpenBottomSheet =
      Array.isArray(actions) &&
      actions.some((action) => action.type === 'openBottomSheet');
    const normalizedActionAlias = payload.action.alias?.toLowerCase();
    const normalizedActionLabel = payload.action.label.trim().toLowerCase();
    const isAddToPlaylistAction =
      hasOpenBottomSheet ||
      normalizedActionAlias === 'add_to_playlist' ||
      normalizedActionLabel === 'add to playlist';
    const shouldKeepDesktopMenuOpenForAddToPlaylist =
      isDesktopLargeDesktop && isAddToPlaylistAction;
    const shouldForceDismissAddToPlaylistInPlayerContext =
      Boolean(hideTriggerButton) &&
      isAddToPlaylistAction &&
      !isDesktopLargeDesktop;

    if (isDesktopLargeDesktop && isAddToPlaylistAction) {
      setDesktopPlaylistPopoverVariant('menu-trigger');
      setPlaylistPopoverAnchorRect(payload.triggerRect);
    }

    if (Array.isArray(actions) && actions.length) {
      await ActionRegistry.getInstance().execute(
        resolveCloudEventActions(actions),
        {
          context: {
            uiComponent,
            entry,
            toggleFavorite: favoriteAction,
            openPlaylistBottomSheet,
            closePlaylistBottomSheet,
            confirmPlaylistDialog,
            editPlaylistCollectionName,
            promptPlaylistTextInput,
          },
        }
      );
    }

    onActionSelect?.(payload);

    if (hasOpenBottomSheet && !shouldForceDismissAddToPlaylistInPlayerContext) {
      return;
    }

    if (
      payload.action.dismissOnAction === false &&
      !shouldForceDismissAddToPlaylistInPlayerContext
    ) {
      return;
    }

    if (shouldKeepDesktopMenuOpenForAddToPlaylist) {
      return;
    }

    if (
      isDesktopLargeDesktop &&
      playlistBottomSheet.isOpen &&
      desktopPlaylistPopoverVariant === 'menu-trigger'
    ) {
      closePlaylistBottomSheet();
    }

    closeMenuAndReset();
  };

  const handleQuickAliasAction = useCallback(
    async ({
      alias,
      triggerElement,
    }: {
      alias: string;
      triggerElement?: HTMLElement;
    }): Promise<void> => {
      const normalizedAlias = alias.trim().toLowerCase();

      if (!normalizedAlias) {
        return;
      }

      if (!isLoggedIn && PLAYLIST_AUTH_ALIASES.has(normalizedAlias)) {
        redirectToLoginWithRedirectBack();
        return;
      }

      if (normalizedAlias === 'add_to_playlist' && playlistBottomSheet.isOpen) {
        if (desktopPlaylistPopoverVariant === 'mini-player') {
          closePlaylistBottomSheet();
          return;
        }

        closePlaylistBottomSheet();
      }

      if (isOpen) {
        closeMenuAndReset();
      }

      const allActions = visibleSections.flatMap((section) => section.actions);
      const matchedAction = findActionByAlias({
        actions: allActions,
        alias: normalizedAlias,
      });

      if (!matchedAction) {
        return;
      }

      const matchedActions = matchedAction.actions;

      if (
        isDesktopLargeDesktop &&
        normalizedAlias === 'add_to_playlist' &&
        triggerElement
      ) {
        const rect = triggerElement.getBoundingClientRect();

        playlistPopoverTriggerRef.current = triggerElement;
        setDesktopPlaylistPopoverVariant('mini-player');
        setPlaylistPopoverAnchorRect({
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      }

      if (Array.isArray(matchedActions) && matchedActions.length > 0) {
        await ActionRegistry.getInstance().execute(
          resolveCloudEventActions(matchedActions),
          {
            context: {
              uiComponent,
              entry,
              openPlaylistBottomSheet,
              closePlaylistBottomSheet,
              confirmPlaylistDialog,
              editPlaylistCollectionName,
              promptPlaylistTextInput,
            },
          }
        );
      }

      onActionSelect?.({
        action: matchedAction,
      });

      const shouldKeepDesktopPlaylistPopoverState =
        isDesktopLargeDesktop && normalizedAlias === 'add_to_playlist';

      if (
        (matchedAction.dismissOnAction !== false || hideTriggerButton) &&
        !shouldKeepDesktopPlaylistPopoverState
      ) {
        closeMenuAndReset();
      }
    },
    [
      closeMenuAndReset,
      closePlaylistBottomSheet,
      confirmPlaylistDialog,
      desktopPlaylistPopoverVariant,
      isLoggedIn,
      isDesktopLargeDesktop,
      editPlaylistCollectionName,
      promptPlaylistTextInput,
      entry,
      hideTriggerButton,
      isOpen,
      onActionSelect,
      openPlaylistBottomSheet,
      playlistBottomSheet.isOpen,
      redirectToLoginWithRedirectBack,
      resolveCloudEventActions,
      uiComponent,
      visibleSections,
    ]
  );

  const handleHeaderActionSelect = (action: PopupMenuHeaderAction): void => {
    onActionSelect?.({
      action: {
        id: action.id,
        label: action.label,
      },
    });
    closeMenuAndReset();
  };

  const headerRowClassName = useMemo(() => {
    if (entry.header.titleAlignment === 'center') {
      return 'popup-menu-header-row-classes popup-menu-header-title-center-classes';
    }

    return 'popup-menu-header-row-classes popup-menu-header-title-left-classes';
  }, [entry.header.titleAlignment]);

  const headerTitleClassName = useMemo(() => {
    if (entry.header.titleAlignment === 'center') {
      return 'popup-menu-title-classes popup-menu-title-center-classes';
    }

    return 'popup-menu-title-classes popup-menu-title-left-classes';
  }, [entry.header.titleAlignment]);

  const handleTriggerToggle = useCallback(
    ({
      target,
      source,
      triggerRect,
    }: {
      target: HTMLElement;
      source: 'pointerdown' | 'mousedown' | 'click' | 'contextmenu';
      triggerRect?: PopupMenuAnchorRect;
    }): void => {
      if (playlistBottomSheet.isOpen) {
        if (desktopPlaylistPopoverVariant === 'menu-trigger' && !isOpen) {
          closePlaylistBottomSheet();
          return;
        }

        closePlaylistBottomSheet();
      }

      if (isOpen) {
        if (source === 'contextmenu') {
          activeMenuTriggerRef.current = target;
          openFromElement(target, triggerRect);
          return;
        }

        lastMenuCloseRef.current = {
          target,
          at: Date.now(),
        };
        closeMenuAndReset();
        return;
      }

      const lastMenuClose = lastMenuCloseRef.current;
      const closedBySameTriggerRecently =
        source !== 'contextmenu' &&
        Boolean(
          lastMenuClose.target && target.contains(lastMenuClose.target)
        ) &&
        Date.now() - lastMenuClose.at < SAME_TRIGGER_REOPEN_GUARD_MS;

      if (closedBySameTriggerRecently) {
        return;
      }

      activeMenuTriggerRef.current = target;
      openFromElement(target, triggerRect);
    },
    [
      closeMenuAndReset,
      closePlaylistBottomSheet,
      desktopPlaylistPopoverVariant,
      isOpen,
      openFromElement,
      playlistBottomSheet.isOpen,
    ]
  );

  const handleTriggerToggleRef = useRef(handleTriggerToggle);
  const handleQuickAliasActionRef = useRef(handleQuickAliasAction);

  useEffect(() => {
    handleTriggerToggleRef.current = handleTriggerToggle;
    handleQuickAliasActionRef.current = handleQuickAliasAction;
  }, [handleQuickAliasAction, handleTriggerToggle]);

  const openRequestId: number | undefined = openRequest?.requestId;

  useEffect(() => {
    if (!openRequest) {
      return;
    }

    if (openRequest.quickActionAlias) {
      void handleQuickAliasActionRef.current({
        alias: openRequest.quickActionAlias,
        triggerElement: openRequest.triggerElement,
      });
      return;
    }

    handleTriggerToggleRef.current({
      target: openRequest.triggerElement,
      source: openRequest.source,
      triggerRect: openRequest.pointer
        ? getPointerAnchorRect(openRequest.pointer)
        : undefined,
    });
    // Runs once per Show Popup Menu request; re-renders with the same request must not re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequestId]);

  useEffect(() => {
    const shouldCloseMenuTriggerPlaylistPopover =
      !isOpen &&
      playlistBottomSheet.isOpen &&
      desktopPlaylistPopoverVariant === 'menu-trigger';

    if (!shouldCloseMenuTriggerPlaylistPopover) {
      return;
    }

    closePlaylistBottomSheet();
  }, [
    closePlaylistBottomSheet,
    desktopPlaylistPopoverVariant,
    isOpen,
    playlistBottomSheet.isOpen,
  ]);

  const playlistDesktopPopoverStyle = useMemo(() => {
    if (!isDesktopLargeDesktop || !playlistBottomSheet.isOpen) {
      return undefined;
    }

    const viewportWidth =
      typeof window === 'undefined' ? 1280 : window.innerWidth;
    const viewportHeight =
      typeof window === 'undefined' ? 720 : window.innerHeight;
    const edgeMargin = 12;
    const popoverWidth =
      desktopPlaylistPopoverVariant === 'menu-trigger' ? 280 : 200;
    const popoverMaxHeight =
      desktopPlaylistPopoverVariant === 'menu-trigger'
        ? Math.min(560, viewportHeight - edgeMargin * 2)
        : Math.min(460, viewportHeight - edgeMargin * 2);
    const horizontalGap = 4;
    const verticalGap = 14;

    const anchor = playlistPopoverAnchorRect || {
      top: viewportHeight / 2 - 20,
      right: viewportWidth / 2 + 20,
      bottom: viewportHeight / 2 + 20,
      left: viewportWidth / 2 - 20,
      width: 40,
      height: 40,
    };

    const openToLeft =
      desktopPlaylistPopoverVariant === 'menu-trigger' ||
      anchor.right + horizontalGap + popoverWidth > viewportWidth;
    const leftCandidate = openToLeft
      ? anchor.left - popoverWidth - horizontalGap
      : anchor.right + horizontalGap;
    const left = Math.max(
      edgeMargin,
      Math.min(leftCandidate, viewportWidth - popoverWidth - edgeMargin)
    );

    if (desktopPlaylistPopoverVariant === 'mini-player') {
      const centeredLeftCandidate =
        anchor.left + anchor.width / 2 - popoverWidth / 2;
      const centeredLeft = Math.max(
        edgeMargin,
        Math.min(
          centeredLeftCandidate,
          viewportWidth - popoverWidth - edgeMargin
        )
      );

      return {
        position: 'fixed' as const,
        bottom: `${Math.max(
          edgeMargin,
          viewportHeight - anchor.top + verticalGap
        )}px`,
        left: `${centeredLeft}px`,
        width: `${popoverWidth}px`,
        maxHeight: `${popoverMaxHeight}px`,
      };
    }

    if (desktopPlaylistPopoverVariant === 'menu-trigger') {
      const menuSurfaceRect = surfaceRef.current?.getBoundingClientRect();

      if (menuSurfaceRect) {
        const spaceOnRight = viewportWidth - menuSurfaceRect.right - edgeMargin;
        const spaceOnLeft = menuSurfaceRect.left - edgeMargin;
        const canOpenRight = spaceOnRight >= popoverWidth + horizontalGap;
        const canOpenLeft = spaceOnLeft >= popoverWidth + horizontalGap;
        const shouldOpenRight = !canOpenLeft && canOpenRight;
        const anchoredLeftCandidate = shouldOpenRight
          ? menuSurfaceRect.right + 2
          : menuSurfaceRect.left - popoverWidth - 2;
        const anchoredLeft = Math.max(
          edgeMargin,
          Math.min(
            anchoredLeftCandidate,
            viewportWidth - popoverWidth - edgeMargin
          )
        );
        const anchoredTop = Math.max(edgeMargin, menuSurfaceRect.top);
        const alignedMaxHeight = Math.max(
          180,
          viewportHeight - anchoredTop - edgeMargin
        );

        return {
          position: 'fixed' as const,
          top: `${anchoredTop}px`,
          left: `${anchoredLeft}px`,
          width: `${popoverWidth}px`,
          maxHeight: `${alignedMaxHeight}px`,
        };
      }
    }

    const topCandidate = anchor.top;
    const top = Math.max(
      edgeMargin,
      Math.min(topCandidate, viewportHeight - popoverMaxHeight - edgeMargin)
    );

    return {
      position: 'fixed' as const,
      top: `${top}px`,
      left: `${left}px`,
      width: `${popoverWidth}px`,
      maxHeight: `${popoverMaxHeight}px`,
    };
  }, [
    desktopPlaylistPopoverVariant,
    isDesktopLargeDesktop,
    playlistBottomSheet.isOpen,
    playlistPopoverAnchorRect,
  ]);

  const isDesktopPlaylistPopover =
    isDesktopLargeDesktop && playlistBottomSheet.isOpen;
  const isMobileTabletSelectionModal =
    !isDesktopPlaylistPopover && playlistBottomSheet.isSelectionMode;

  const handlePlaylistBottomSheetItemSelect = useCallback(
    async (item: PlaylistBottomSheetActionItem): Promise<void> => {
      const isSelectionToggleItem =
        playlistBottomSheet.isSelectionMode &&
        item.interactionType !== 'management';

      if (isSelectionToggleItem && !isDesktopLargeDesktop) {
        const previousSelected = Boolean(playlistStagedSelectionById[item.id]);
        const nextSelected = !previousSelected;
        const originalSelected = Boolean(item.selected);

        setPlaylistStagedSelectionById((prevState) => ({
          ...prevState,
          [item.id]: nextSelected,
        }));

        setPlaylistStagedDirtyIds((prevState) => {
          const dirtyIdSet = new Set(prevState);

          if (nextSelected === originalSelected) {
            dirtyIdSet.delete(item.id);
          } else {
            dirtyIdSet.add(item.id);
          }

          return Array.from(dirtyIdSet);
        });

        return;
      }

      if (isSelectionToggleItem && isDesktopLargeDesktop) {
        const hasStagedValue = Object.prototype.hasOwnProperty.call(
          playlistStagedSelectionById,
          item.id
        );
        const currentSelected = hasStagedValue
          ? Boolean(playlistStagedSelectionById[item.id])
          : Boolean(item.selected);

        setPlaylistStagedSelectionById((prevState) => ({
          ...prevState,
          [item.id]: !currentSelected,
        }));

        setPlaylistStagedDirtyIds((prevState) => {
          return prevState.filter((dirtyId) => dirtyId !== item.id);
        });
      }

      await executeBottomSheetActionItem(item);
    },
    [
      executeBottomSheetActionItem,
      isDesktopLargeDesktop,
      playlistBottomSheet.isSelectionMode,
      playlistStagedSelectionById,
    ]
  );

  const handleCreatePlaylistActionItemSelect = useCallback(async () => {
    const createPlaylistActionItem =
      playlistBottomSheet.createPlaylistActionItem;

    if (!createPlaylistActionItem) {
      return;
    }

    const createAction = createPlaylistActionItem.actions.find((action) =>
      isCreatePlaylistAction(action)
    );
    const createActionOptions = isRecord(createAction?.options)
      ? createAction?.options
      : undefined;
    const createActionData = isRecord(createActionOptions?.data)
      ? createActionOptions?.data
      : undefined;
    const initialName = getStringValue(createActionData?.name) || '';

    setCreateDialogState({
      actionItem: createPlaylistActionItem,
      title: playlistLocalizations.createNewPlaylistTitle,
      submitButtonText: playlistLocalizations.createPlaylistButtonLabel,
      inputTitle: playlistLocalizations.playlistNameInputTitle,
      inputPlaceholder: playlistLocalizations.playlistNameHint,
    });
    setCreateInputValue(initialName);
  }, [
    playlistLocalizations.createNewPlaylistTitle,
    playlistLocalizations.createPlaylistButtonLabel,
    playlistLocalizations.playlistNameHint,
    playlistLocalizations.playlistNameInputTitle,
    playlistBottomSheet.createPlaylistActionItem,
  ]);

  const handleFeedAddSelect = useCallback(async (): Promise<void> => {
    const addActions = playlistBottomSheet.addActions;

    if (!addActions?.length) {
      return;
    }

    const refreshFeedEntry = playlistBottomSheet.sourceItemsUrl
      ? { _parentFeedPath: playlistBottomSheet.sourceItemsUrl }
      : undefined;

    const executionResult = await ActionRegistry.getInstance().execute(
      addActions,
      {
        context: {
          uiComponent,
          entry,
          feedEntry: refreshFeedEntry,
          openPlaylistBottomSheet,
          closePlaylistBottomSheet,
          confirmPlaylistDialog,
          editPlaylistCollectionName,
          promptPlaylistTextInput,
        },
      }
    );

    if (executionResult.final !== ActionResult.Success) {
      return;
    }

    if (!playlistBottomSheet.sourceItemsUrl) {
      return;
    }

    const refreshedData = await loadPlaylistBottomSheetData(
      playlistBottomSheet.sourceItemsUrl,
      { forceFresh: true }
    );

    if (
      refreshedData.items.length === 0 &&
      !refreshedData.createPlaylistActionItem &&
      !refreshedData.addActions?.length
    ) {
      closePlaylistBottomSheet();
      return;
    }

    setPlaylistBottomSheet((prevState) => ({
      ...prevState,
      isSelectionMode: refreshedData.isSelectionMode,
      createPlaylistActionItem: refreshedData.createPlaylistActionItem,
      addActions: refreshedData.addActions,
      items: refreshedData.items,
    }));
    initializeStagedSelection(refreshedData.items);
  }, [
    closePlaylistBottomSheet,
    confirmPlaylistDialog,
    editPlaylistCollectionName,
    entry,
    initializeStagedSelection,
    loadPlaylistBottomSheetData,
    openPlaylistBottomSheet,
    playlistBottomSheet.addActions,
    playlistBottomSheet.sourceItemsUrl,
    promptPlaylistTextInput,
    uiComponent,
  ]);

  const handlePlaylistBottomSheetDone = useCallback(async (): Promise<void> => {
    if (!playlistBottomSheet.isSelectionMode) {
      closePlaylistBottomSheet();
      return;
    }

    if (playlistStagedDirtyIds.length === 0) {
      closePlaylistBottomSheet();
      return;
    }

    const changedItems = playlistBottomSheet.items.filter((item) => {
      return playlistStagedDirtyIds.includes(item.id);
    });

    for (const changedItem of changedItems) {
      const normalizedActions = resolveCloudEventActions(changedItem.actions);

      const executionResult = await ActionRegistry.getInstance().execute(
        normalizedActions,
        {
          context: {
            uiComponent,
            entry,
            openPlaylistBottomSheet,
            closePlaylistBottomSheet,
            confirmPlaylistDialog,
            editPlaylistCollectionName,
            promptPlaylistTextInput,
          },
        }
      );

      if (executionResult.final === ActionResult.Error) {
        return;
      }

      if (executionResult.final === ActionResult.Cancel) {
        return;
      }
    }

    if (playlistBottomSheet.sourceItemsUrl) {
      try {
        await loadPlaylistBottomSheetData(playlistBottomSheet.sourceItemsUrl, {
          forceFresh: true,
        });
      } catch {
        // Non-blocking: modal should still close if refresh fails.
      }
    }

    closePlaylistBottomSheet();
    closeMenuAndReset();
  }, [
    closeMenuAndReset,
    closePlaylistBottomSheet,
    confirmPlaylistDialog,
    editPlaylistCollectionName,
    promptPlaylistTextInput,
    entry,
    loadPlaylistBottomSheetData,
    openPlaylistBottomSheet,
    playlistBottomSheet.isSelectionMode,
    playlistBottomSheet.items,
    playlistBottomSheet.sourceItemsUrl,
    playlistStagedDirtyIds,
    resolveCloudEventActions,
    uiComponent,
  ]);

  const filteredPlaylistBottomSheetItems = useMemo(() => {
    const normalizedQuery = playlistSearchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return playlistBottomSheet.items;
    }

    return playlistBottomSheet.items.filter((item) => {
      const labelMatch = item.title.toLowerCase().includes(normalizedQuery);
      const subtitleMatch = item.subtitle
        ? item.subtitle.toLowerCase().includes(normalizedQuery)
        : false;

      return labelMatch || subtitleMatch;
    });
  }, [playlistBottomSheet.items, playlistSearchQuery]);

  const normalizedConfirmDialogTitle =
    confirmDialogState?.title.toLowerCase() || '';
  const normalizedConfirmDialogOkButtonText =
    confirmDialogState?.okButtonText.toLowerCase() || '';
  const isDestructiveConfirmDialog =
    normalizedConfirmDialogOkButtonText.includes('clear') ||
    normalizedConfirmDialogTitle.includes('clear') ||
    normalizedConfirmDialogOkButtonText.includes('delete') ||
    normalizedConfirmDialogTitle.includes('delete');
  const normalizedRenameDialogName = renameInputValue.trim();
  const isRenameDialogSubmitDisabled = normalizedRenameDialogName.length === 0;
  const normalizedCreateDialogName = createInputValue.trim();
  const isCreateDialogSubmitDisabled = normalizedCreateDialogName.length === 0;
  const isPlaylistNamingModalOpen = Boolean(
    createDialogState || renameDialogState
  );
  const isEditPlaylistDetailsOpen = Boolean(
    playlistBottomSheet.sourceItemsUrl?.includes('editable=true')
  );
  const hideActionMenu = isPlaylistNamingModalOpen || isEditPlaylistDetailsOpen;
  const popupMenuScopedClassName = uiComponent.id
    ? `popup-menu-${uiComponent.id}`
    : '';

  const hasSingleSection: boolean = visibleSections.length === 1;
  const playlistConfigForAssets = rootLoaderData?.playlistConfig as
    | Record<string, Record<string, unknown>>
    | undefined;
  const createNewPlaylistAssetSrc: string | undefined =
    getCreateNewPlaylistAssetSrc({
      playlistConfig: playlistConfigForAssets,
      isDesktop: isDesktopLargeDesktop,
    });

  const withPopupScopeClass = (baseClassName: string): string => {
    if (!popupMenuScopedClassName) {
      return baseClassName;
    }

    return `${baseClassName} ${popupMenuScopedClassName}`;
  };

  const canUseDom = typeof document !== 'undefined';
  const renderPlaylistLayer = useCallback(
    (node: JSX.Element): JSX.Element => {
      const scopedNode = <PlaylistVarsScope>{node}</PlaylistVarsScope>;

      if (!canUseDom) {
        return scopedNode;
      }

      if (!hideTriggerButton) {
        return scopedNode;
      }

      return createPortal(scopedNode, document.body);
    },
    [canUseDom, hideTriggerButton]
  );

  return (
    <section ref={rootRef} style={componentStyle}>
      {!hideTriggerButton ? (
        <button
          ref={triggerRef}
          type="button"
          className="rounded-md border border-slate-400 bg-slate-50 px-3 py-2 text-sm text-slate-800"
          onClick={(event) => {
            handleTriggerToggle({
              target: event.currentTarget,
              source: 'click',
            });
          }}
          onContextMenu={(event) => {
            event.preventDefault();
            handleTriggerToggle({
              target: event.currentTarget,
              source: 'contextmenu',
              triggerRect: getPointerAnchorRect({
                x: event.clientX,
                y: event.clientY,
              }),
            });
          }}
        >
          <span className="inline-flex items-center gap-2">
            {triggerIconSrc ? (
              <span
                aria-hidden
                className="popup-menu-item-leading-icon-classes"
              >
                <img
                  src={triggerIconSrc}
                  alt=""
                  className="h-full w-full object-contain"
                />
              </span>
            ) : null}

            <span>{entry.triggerLabel}</span>
          </span>
        </button>
      ) : null}
      {isOpen && !hideActionMenu ? (
        <section
          className={withPopupScopeClass('popup-menu-root-classes')}
          style={computedRootStyle}
          data-popup-mode={isDesktopLargeDesktop ? 'desktop' : 'mobile'}
        >
          <PopupMenuOverlay
            mode={overlayMode}
            ariaLabel="Close popup menu"
            className="popup-menu-overlay-classes"
            insideRefs={menuOverlayInsideRefs}
            onDismiss={dismissMenu}
            style={{
              position: 'absolute',
              inset: 0,
              marginTop: 0,
              height: '100%',
              border: 'none',
              width: '100%',
              pointerEvents: 'auto',
            }}
          />

          <div
            ref={surfaceRef}
            className={`popup-menu-surface-classes ${
              isDesktopLargeDesktop
                ? 'popup-menu-surface-desktop-classes'
                : 'popup-menu-surface-mobile-classes'
            }`}
            style={surfaceStyle}
            role="menu"
            onKeyDown={handleMenuKeyDown}
          >
            <header className="popup-menu-header-classes">
              <div className={headerRowClassName}>
                <div className="popup-menu-header-side-classes">
                  {entry.header.leadingAction ? (
                    <button
                      type="button"
                      className="popup-menu-header-action-button-classes"
                      onClick={() => {
                        const leadingAction = entry.header.leadingAction;

                        if (!leadingAction) return;

                        handleHeaderActionSelect(leadingAction);
                      }}
                    >
                      {entry.header.leadingAction.label}
                    </button>
                  ) : null}
                </div>

                <h3 className={headerTitleClassName}>
                  {activeTitle ?? entry.title}
                </h3>

                <div className="popup-menu-header-side-classes popup-menu-header-side-right-classes">
                  {entry.header.trailingAction ? (
                    <button
                      type="button"
                      className="popup-menu-header-action-button-classes"
                      onClick={() => {
                        const trailingAction = entry.header.trailingAction;

                        if (!trailingAction) return;

                        handleHeaderActionSelect(trailingAction);
                      }}
                    >
                      {entry.header.trailingAction.label}
                    </button>
                  ) : null}
                </div>
              </div>

              {entry.subtitle ? (
                <p className="popup-menu-subtitle-classes">{entry.subtitle}</p>
              ) : null}
            </header>

            {hasSingleSection ? (
              <div
                className="popup-menu-divider-classes"
                data-testid="popup-menu-header-divider"
              />
            ) : null}

            <div className="popup-menu-body-classes">
              {!isDesktopLargeDesktop && canGoBack ? (
                <div className="popup-menu-back-section-classes popup-menu-section-classes">
                  <div className="popup-menu-action-wrapper-classes">
                    <div className="popup-menu-item-classes">
                      <button
                        type="button"
                        className="popup-menu-action-control-classes"
                        role="menuitem"
                        onClick={goBack}
                      >
                        <span
                          className="popup-menu-back-icon-classes popup-menu-item-trailing-icon-classes"
                          aria-hidden
                        >
                          {'<'}
                        </span>
                        <span className="popup-menu-item-label-classes">
                          {currentMobileLevelLabel
                            ? `Back to ${currentMobileLevelLabel}`
                            : 'Back'}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {visibleSections.map((section) => {
                return (
                  <div key={section.id} className="popup-menu-section-classes">
                    {section.label && !hasSingleSection ? (
                      <div className="popup-menu-section-label-classes">
                        {section.label}
                      </div>
                    ) : null}

                    {section.actions.map((action) => {
                      return renderAction({
                        action,
                        actionPath: [action.id],
                        getIconSrc,
                        isDesktopLargeDesktop,
                        isDesktopActionExpanded,
                        onDesktopActionHover: handleDesktopActionHover,
                        onNestedActionIntent: handleActionSelectionIntent,
                        onSelect: handleActionSelect,
                      });
                    })}
                  </div>
                );
              })}

              {!isDesktopLargeDesktop ? (
                <div className="popup-menu-divider-classes" />
              ) : null}

              {!isDesktopLargeDesktop && entry.closeButton ? (
                <div className="w-full">
                  <button
                    type="button"
                    className="popup-menu-close-button-classes"
                    role="menuitem"
                    onClick={() => {
                      closeMenuAndReset();
                    }}
                  >
                    {entry.closeButtonLabel}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}
      {playlistBottomSheet.isOpen && !isPlaylistNamingModalOpen
        ? renderPlaylistLayer(
            <section
              ref={playlistLayerRef}
              className={withPopupScopeClass(
                `popup-menu-playlist-modal-root-classes ${
                  isDesktopPlaylistPopover
                    ? 'popup-menu-playlist-popover-root-classes'
                    : ''
                }`
              )}
              role="dialog"
              aria-modal={!isDesktopPlaylistPopover}
            >
              <PopupMenuOverlay
                mode={overlayMode}
                ariaLabel="Close playlist modal"
                className="popup-menu-playlist-modal-overlay-classes"
                insideRefs={playlistOverlayInsideRefs}
                onDismiss={closePlaylistBottomSheet}
              />
              <div
                className={`popup-menu-playlist-modal-surface-classes ${
                  !isDesktopPlaylistPopover
                    ? 'popup-menu-playlist-sheet-surface-classes'
                    : ''
                } ${
                  isDesktopPlaylistPopover
                    ? 'popup-menu-playlist-popover-surface-classes'
                    : ''
                }`}
                data-playlist-popover-variant={desktopPlaylistPopoverVariant}
                data-theme-v2={rootLoaderData?.isThemeV2 ? 'true' : undefined}
                style={playlistDesktopPopoverStyle}
              >
                {!isDesktopPlaylistPopover ? (
                  <header
                    className={`popup-menu-playlist-modal-header-classes ${
                      isMobileTabletSelectionModal
                        ? 'popup-menu-playlist-modal-header-selection-classes'
                        : ''
                    }`}
                  >
                    {isMobileTabletSelectionModal ? (
                      <button
                        type="button"
                        className="popup-menu-playlist-header-chip-classes popup-menu-playlist-header-chip-left-classes"
                        data-testid="playlist-selection-cancel"
                        onClick={closePlaylistBottomSheet}
                      >
                        Cancel
                      </button>
                    ) : null}

                    {playlistBottomSheet.title ? (
                      <h3 className="popup-menu-playlist-modal-title-classes">
                        {playlistBottomSheet.title}
                      </h3>
                    ) : null}
                    {playlistBottomSheet.subtitle ? (
                      <p className="popup-menu-playlist-modal-subtitle-classes">
                        {playlistBottomSheet.subtitle}
                      </p>
                    ) : null}
                    {playlistBottomSheet.sectionTitle ? (
                      <p className="popup-menu-playlist-modal-section-title-classes">
                        {playlistBottomSheet.sectionTitle}
                      </p>
                    ) : null}

                    {isMobileTabletSelectionModal ? (
                      <button
                        type="button"
                        className="popup-menu-playlist-header-chip-classes popup-menu-playlist-header-chip-right-classes"
                        data-testid="playlist-selection-done"
                        onClick={() => {
                          void handlePlaylistBottomSheetDone();
                        }}
                      >
                        Done
                      </button>
                    ) : null}
                  </header>
                ) : null}

                <div className="popup-menu-playlist-modal-body-classes">
                  {isDesktopPlaylistPopover &&
                  (playlistBottomSheet.addActions?.length ||
                    playlistBottomSheet.createPlaylistActionItem) ? (
                    <button
                      type="button"
                      className="popup-menu-playlist-popover-create-row-classes"
                      data-playlist-create-row="true"
                      onClick={() => {
                        if (playlistBottomSheet.addActions?.length) {
                          void handleFeedAddSelect();
                          return;
                        }

                        void handleCreatePlaylistActionItemSelect();
                      }}
                    >
                      <span className="popup-menu-playlist-popover-create-icon-classes">
                        {createNewPlaylistAssetSrc ? (
                          <img
                            src={createNewPlaylistAssetSrc}
                            alt=""
                            aria-hidden
                            className="popup-menu-playlist-create-icon-image-classes"
                            data-testid="playlist-create-row-icon"
                          />
                        ) : (
                          '+'
                        )}
                      </span>
                      <span>
                        {playlistBottomSheet.addActions?.length
                          ? playlistLocalizations.createNewPlaylistButtonLabel
                          : playlistBottomSheet.createPlaylistActionItem
                              ?.title ||
                            playlistLocalizations.createNewPlaylistButtonLabel}
                      </span>
                    </button>
                  ) : null}

                  {!isDesktopPlaylistPopover &&
                  (playlistBottomSheet.addActions?.length ||
                    playlistBottomSheet.createPlaylistActionItem) ? (
                    <button
                      type="button"
                      className="popup-menu-playlist-confirm-primary-classes popup-menu-playlist-create-cta-classes"
                      data-playlist-create-cta="true"
                      onClick={() => {
                        if (playlistBottomSheet.addActions?.length) {
                          void handleFeedAddSelect();
                          return;
                        }

                        void handleCreatePlaylistActionItemSelect();
                      }}
                    >
                      {createNewPlaylistAssetSrc ? (
                        <img
                          src={createNewPlaylistAssetSrc}
                          alt=""
                          aria-hidden
                          className="popup-menu-playlist-create-icon-image-classes popup-menu-playlist-create-cta-icon-classes"
                          data-testid="playlist-create-cta-icon"
                        />
                      ) : null}
                      {playlistBottomSheet.addActions?.length
                        ? playlistLocalizations.createNewPlaylistButtonLabel
                        : playlistBottomSheet.createPlaylistActionItem?.title ||
                          playlistLocalizations.createNewPlaylistButtonLabel}
                    </button>
                  ) : null}

                  {isDesktopPlaylistPopover &&
                  desktopPlaylistPopoverVariant === 'mini-player' ? (
                    <p className="popup-menu-playlist-popover-section-title-classes">
                      {playlistLocalizations.recentPlaylistsSectionTitle}
                    </p>
                  ) : null}

                  {playlistBottomSheet.items.length &&
                  (desktopPlaylistPopoverVariant !== 'mini-player' ||
                    !isDesktopPlaylistPopover) ? (
                    <div className="popup-menu-playlist-search-field-classes">
                      <span
                        className="popup-menu-playlist-search-icon-classes"
                        aria-hidden
                      />
                      <input
                        type="search"
                        className="popup-menu-playlist-search-input-classes"
                        placeholder={
                          playlistLocalizations.searchPlaylistsFieldHint
                        }
                        value={playlistSearchQuery}
                        onChange={(event) => {
                          setPlaylistSearchQuery(event.target.value);
                        }}
                      />
                    </div>
                  ) : null}

                  {filteredPlaylistBottomSheetItems.map((item) => {
                    const isSelectionToggleItem =
                      playlistBottomSheet.isSelectionMode &&
                      item.interactionType !== 'management';
                    const stagedSelected =
                      isSelectionToggleItem &&
                      Object.prototype.hasOwnProperty.call(
                        playlistStagedSelectionById,
                        item.id
                      )
                        ? playlistStagedSelectionById[item.id]
                        : item.selected;
                    const selectionAssetSrc: string | undefined =
                      isSelectionToggleItem
                        ? getPlaylistSelectionAssetSrc({
                            playlistConfig: playlistConfigForAssets,
                            isDesktop: isDesktopLargeDesktop,
                            isSelected: Boolean(stagedSelected),
                          })
                        : undefined;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        className="popup-menu-playlist-modal-item-classes"
                        data-playlist-item-id={item.id}
                        data-playlist-item-selected={
                          isSelectionToggleItem && stagedSelected
                            ? 'true'
                            : 'false'
                        }
                        data-playlist-item-interaction={
                          isSelectionToggleItem ? 'selection' : 'management'
                        }
                        onClick={() => {
                          void handlePlaylistBottomSheetItemSelect(item);
                        }}
                      >
                        {item.imageSrc ? (
                          <img
                            src={item.imageSrc}
                            alt=""
                            className="popup-menu-item-leading-icon-classes"
                          />
                        ) : null}
                        <span className="popup-menu-playlist-modal-item-text-classes">
                          <span className="popup-menu-playlist-modal-item-title-classes">
                            {item.title}
                          </span>
                          {item.subtitle ? (
                            <span className="popup-menu-playlist-modal-item-subtitle-classes">
                              {item.subtitle}
                            </span>
                          ) : null}
                        </span>
                        {isSelectionToggleItem && selectionAssetSrc ? (
                          <img
                            src={selectionAssetSrc}
                            alt=""
                            aria-hidden
                            className="popup-menu-playlist-modal-item-selection-asset-classes"
                            data-playlist-item-selected={
                              stagedSelected ? 'true' : 'false'
                            }
                            data-testid="playlist-item-selection-asset"
                          />
                        ) : isSelectionToggleItem ? (
                          <span
                            className="popup-menu-playlist-modal-item-selection-indicator-classes"
                            data-playlist-item-selected={
                              stagedSelected ? 'true' : 'false'
                            }
                            aria-hidden
                          >
                            {stagedSelected ? '✓' : ''}
                          </span>
                        ) : (
                          <span
                            className="popup-menu-playlist-modal-item-chevron-classes"
                            aria-hidden
                          >
                            {'›'}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {!isDesktopPlaylistPopover &&
                !playlistBottomSheet.isSelectionMode ? (
                  <div className="popup-menu-playlist-modal-footer-classes">
                    <button
                      type="button"
                      className="popup-menu-playlist-modal-close-classes"
                      onClick={closePlaylistBottomSheet}
                    >
                      Close
                    </button>
                  </div>
                ) : null}
              </div>
            </section>
          )
        : null}
      {confirmDialogState
        ? renderPlaylistLayer(
            <section
              ref={confirmDialogLayerRef}
              className={withPopupScopeClass(
                'popup-menu-playlist-modal-root-classes'
              )}
              role="dialog"
              aria-modal="true"
            >
              <button
                type="button"
                className="popup-menu-playlist-modal-overlay-classes"
                onClick={() => {
                  confirmResolverRef.current?.(false);
                  confirmResolverRef.current = null;
                  setConfirmDialogState(null);
                }}
                aria-label="Dismiss confirmation"
              />
              <div
                className="popup-menu-playlist-confirm-surface-classes"
                data-confirm-variant={
                  isDestructiveConfirmDialog ? 'destructive' : 'default'
                }
              >
                <h3 className="popup-menu-playlist-modal-title-classes">
                  {confirmDialogState.title}
                </h3>
                <p
                  className="popup-menu-playlist-confirm-message-classes"
                  role={isDestructiveConfirmDialog ? 'alert' : undefined}
                >
                  {confirmDialogState.message}
                </p>
                <div className="popup-menu-playlist-confirm-actions-classes">
                  <button
                    type="button"
                    className="popup-menu-playlist-confirm-secondary-classes"
                    onClick={() => {
                      confirmResolverRef.current?.(false);
                      confirmResolverRef.current = null;
                      setConfirmDialogState(null);
                    }}
                  >
                    {confirmDialogState.cancelButtonText}
                  </button>
                  <button
                    type="button"
                    className="popup-menu-playlist-confirm-primary-classes"
                    onClick={() => {
                      confirmResolverRef.current?.(true);
                      confirmResolverRef.current = null;
                      setConfirmDialogState(null);
                    }}
                  >
                    {confirmDialogState.okButtonText}
                  </button>
                </div>
              </div>
            </section>
          )
        : null}
      {renameDialogState
        ? renderPlaylistLayer(
            <section
              ref={renameDialogLayerRef}
              className={withPopupScopeClass(
                'popup-menu-playlist-modal-root-classes'
              )}
              role="dialog"
              aria-modal="true"
            >
              <button
                type="button"
                className="popup-menu-playlist-modal-overlay-classes"
                onClick={() => {
                  renameResolverRef.current?.(false);
                  renameResolverRef.current = null;
                  setRenameDialogState(null);
                }}
                aria-label="Dismiss rename dialog"
              />
              <div className="popup-menu-playlist-rename-dialog-surface-classes">
                <header className="popup-menu-playlist-create-dialog-header-classes">
                  <h3 className="popup-menu-playlist-create-dialog-title-classes">
                    {renameDialogState.headerTitle || 'Edit Playlist Name'}
                  </h3>
                  <button
                    type="button"
                    className="popup-menu-playlist-create-dialog-close-classes"
                    onClick={() => {
                      renameResolverRef.current?.(false);
                      renameResolverRef.current = null;
                      setRenameDialogState(null);
                    }}
                    aria-label="Close rename dialog"
                  >
                    ×
                  </button>
                </header>
                <div className="popup-menu-playlist-rename-input-block-classes">
                  <label className="popup-menu-playlist-create-dialog-input-label-classes">
                    {renameDialogState.inputLabel ||
                      playlistLocalizations.playlistNameInputTitle}
                  </label>
                  <input
                    ref={renameDialogInputRef}
                    className="popup-menu-playlist-rename-input-classes"
                    value={renameInputValue}
                    maxLength={renameDialogState.maxLength}
                    placeholder={
                      renameDialogState.collectOnly &&
                      renameDialogState.inputLabel
                        ? renameDialogState.inputLabel
                        : playlistLocalizations.playlistNameHint
                    }
                    onChange={(event) => {
                      setRenameInputValue(event.target.value);
                    }}
                  />
                </div>
                <div className="popup-menu-playlist-create-dialog-actions-classes">
                  <button
                    type="button"
                    className="popup-menu-playlist-create-dialog-submit-classes"
                    disabled={isRenameDialogSubmitDisabled}
                    aria-disabled={isRenameDialogSubmitDisabled}
                    onClick={async () => {
                      if (isRenameDialogSubmitDisabled) {
                        return;
                      }

                      if (renameDialogState.collectOnly) {
                        renameResolverRef.current?.(normalizedRenameDialogName);
                        renameResolverRef.current = null;
                        setRenameDialogState(null);
                        return;
                      }

                      const executionResult =
                        await ActionRegistry.getInstance().execute(
                          resolveCloudEventActions([
                            {
                              type: 'sendCloudEvent',
                              options: {
                                url: '@{ctx/cloud_events_url}',
                                type: 'com.applicaster.collection.rename.v1',
                                subject: 'collection_mutation',
                                data: {
                                  collectionId: renameDialogState.collectionId,
                                  name: normalizedRenameDialogName,
                                },
                              },
                            },
                            {
                              type: 'refreshComponent',
                              options: {},
                            },
                          ]),
                          {
                            context: {
                              uiComponent,
                              entry,
                              feedEntry: playlistBottomSheet.sourceItemsUrl
                                ? {
                                    _parentFeedPath:
                                      playlistBottomSheet.sourceItemsUrl,
                                  }
                                : undefined,
                              openPlaylistBottomSheet,
                              closePlaylistBottomSheet,
                              confirmPlaylistDialog,
                              editPlaylistCollectionName,
                            },
                          }
                        );

                      if (executionResult.final !== ActionResult.Success) {
                        renameResolverRef.current?.(false);
                        renameResolverRef.current = null;
                        setRenameDialogState(null);
                        return;
                      }

                      if (entry?.id === renameDialogState.collectionId) {
                        setActiveTitle(normalizedRenameDialogName);
                      }

                      setPlaylistBottomSheet((prev) => ({
                        ...prev,
                        items: prev.items.map((item) =>
                          item.id === renameDialogState.collectionId
                            ? { ...item, title: normalizedRenameDialogName }
                            : item
                        ),
                      }));
                      renameResolverRef.current?.(true);
                      renameResolverRef.current = null;
                      setRenameDialogState(null);
                    }}
                  >
                    {renameDialogState.buttonLabel || 'Save'}
                  </button>
                </div>
              </div>
            </section>
          )
        : null}
      {createDialogState
        ? renderPlaylistLayer(
            <section
              ref={createDialogLayerRef}
              className={withPopupScopeClass(
                'popup-menu-playlist-modal-root-classes popup-menu-playlist-modal-root-centered-classes'
              )}
              role="dialog"
              aria-modal="true"
            >
              <button
                type="button"
                className="popup-menu-playlist-modal-overlay-classes"
                onClick={() => {
                  setCreateDialogState(null);
                  setCreateInputValue('');
                }}
                aria-label="Dismiss create playlist dialog"
              />
              <div className="popup-menu-playlist-create-dialog-surface-classes">
                <header className="popup-menu-playlist-create-dialog-header-classes">
                  <h3 className="popup-menu-playlist-create-dialog-title-classes">
                    {createDialogState.title}
                  </h3>
                  <button
                    type="button"
                    className="popup-menu-playlist-create-dialog-close-classes"
                    onClick={() => {
                      setCreateDialogState(null);
                      setCreateInputValue('');
                    }}
                    aria-label="Close create playlist dialog"
                  >
                    ×
                  </button>
                </header>

                <div className="popup-menu-playlist-create-dialog-input-block-classes">
                  <label className="popup-menu-playlist-create-dialog-input-label-classes">
                    {createDialogState.inputTitle}
                  </label>
                  <input
                    ref={createDialogInputRef}
                    className="popup-menu-playlist-create-dialog-input-classes"
                    value={createInputValue}
                    placeholder={createDialogState.inputPlaceholder}
                    onChange={(event) => {
                      setCreateInputValue(event.target.value);
                    }}
                  />
                </div>

                <div className="popup-menu-playlist-create-dialog-actions-classes">
                  <button
                    type="button"
                    className="popup-menu-playlist-create-dialog-submit-classes"
                    disabled={isCreateDialogSubmitDisabled}
                    aria-disabled={isCreateDialogSubmitDisabled}
                    onClick={async () => {
                      const nextName = normalizedCreateDialogName;

                      if (isCreateDialogSubmitDisabled) {
                        return;
                      }

                      const actionsWithPlaylistName =
                        createDialogState.actionItem.actions.map((action) => {
                          if (!isCreatePlaylistAction(action)) {
                            return action;
                          }

                          const actionOptions = isRecord(action.options)
                            ? action.options
                            : {};
                          const actionData = isRecord(actionOptions.data)
                            ? actionOptions.data
                            : {};

                          return {
                            ...action,
                            options: {
                              ...actionOptions,
                              data: {
                                ...actionData,
                                name: nextName,
                                title: nextName,
                                collectionName: nextName,
                                playlistName: nextName,
                              },
                            },
                          };
                        });

                      await executeBottomSheetActionItem(
                        createDialogState.actionItem,
                        actionsWithPlaylistName
                      );

                      setCreateDialogState(null);
                      setCreateInputValue('');
                    }}
                  >
                    {createDialogState.submitButtonText}
                  </button>
                </div>
              </div>
            </section>
          )
        : null}
    </section>
  );
}
